import React, { useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { Clock, ThumbsUp, ThumbsDown, Droplets } from "lucide-react-native";
import { MoonFilterChip, MoonFormField } from "@louise/moon-ui";
import {
  MedicalHistoryTextField,
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { useTranslations } from "@src/packages/i18n";
import { useSexualHealth } from "../SexualHealthContext";
import { buildSexualHealthNavigationStack } from "../buildSexualHealthNavigationStack";
import { FREQUENCY_OPTIONS } from "../options";
import type { SexualHealthStackParamList } from "../SexualHealthNavigator";
import { useMedicalHistoryTheme } from "../../../theme/MedicalHistoryTheme";

export function LubricantsScreen() {
  const t = useTranslations();
  const { state, updateState, save } = useSexualHealth();
  const navigation =
    useNavigation<NavigationProp<SexualHealthStackParamList>>();
  const theme = useMedicalHistoryTheme();

  const selection = useMemo(() => {
    if (state.usesLubricant == null) return null;
    return state.usesLubricant ? "yes" : "no";
  }, [state.usesLubricant]);

  const handleChange = (value: "yes" | "no") => {
    if (value === "no") {
      updateState({
        usesLubricant: false,
        lubricantName: null,
        lubricantUseFrequency: null,
      });
    } else {
      updateState({ usesLubricant: true });
    }
  };

  const handleSave = async () => {
    await save();
    const stack = buildSexualHealthNavigationStack(state);
    const currentIdx = stack.indexOf("Lubricants");
    const nextRoute = stack[currentIdx + 1];
    if (nextRoute) {
      navigation.navigate(nextRoute);
    }
  };

  return (
    <QuestionLayout
      title={t("med_history.gynae.sexual_health.lubricants_title")}
      subtitle={t("med_history.gynae.sexual_health.lubricants_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={selection === null}
      saveLabel={t("med_history.gynae.sexual_health.continue")}
    >
      <View style={styles.container}>
        <View style={styles.yesNoRow}>
          <SquareOptionButton
            icon={ThumbsUp}
            label={t("med_history.shared.yes")}
            isSelected={selection === "yes"}
            onPress={() => handleChange("yes")}
            iconSize={24}
            containerSize={48}
            labelSize="lg"
          />
          <SquareOptionButton
            icon={ThumbsDown}
            label={t("med_history.shared.no")}
            isSelected={selection === "no"}
            onPress={() => handleChange("no")}
            iconSize={24}
            containerSize={48}
            labelSize="lg"
          />
        </View>

        {selection === "yes" && (
          <View style={styles.followUp}>
            <MoonFormField
              label={t(
                "med_history.gynae.sexual_health.lubricants_frequency_label",
              )}
              labelIcon={Clock}
              style={styles.frequencyField}
            >
              <View style={styles.frequencyRow}>
                {FREQUENCY_OPTIONS.map((option) => (
                  <MoonFilterChip
                    key={option.id}
                    label={t(option.labelKey)}
                    selected={state.lubricantUseFrequency === option.id}
                    onPress={() =>
                      updateState({ lubricantUseFrequency: option.id })
                    }
                    tone="glass"
                    showCheck={false}
                    testID={`lubricants-frequency-${option.id}`}
                  />
                ))}
              </View>
            </MoonFormField>

            <MedicalHistoryTextField
              label={t("med_history.gynae.sexual_health.lubricants_name_label")}
              labelIcon={Droplets}
              placeholder={t(
                "med_history.gynae.sexual_health.lubricants_name_placeholder",
              )}
              value={state.lubricantName ?? ""}
              onChangeText={(text) =>
                updateState({ lubricantName: text || null })
              }
              appearance={theme.inputAppearance}
              style={{ width: "100%" }}
            />
          </View>
        )}
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
  },
  yesNoRow: {
    flexDirection: "row",
    gap: 12,
    width: "100%",
    marginBottom: 24,
  },
  followUp: {
    width: "100%",
  },
  frequencyRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  frequencyField: {
    marginBottom: 8,
  },
});
